import React, { Suspense, lazy, useState, useEffect, useRef } from "react";
import { Bookmark, Share2, Pin, EyeOff, MoreHorizontal, Pencil, Trash2, Flag, Ban, Instagram, Film, Link2, X as XIcon } from "lucide-react";
import { renderPostCard } from "./exportPostImage";
import ExportPostModal from "./ExportPostModal";
import { haptic as bamHaptic, nativeShare as bamNativeShare, openExternal } from "../../utils/nativeBridge";
import { GOLD, getAC } from "../constants/theme";
import { Ic } from "../constants/icons";
import { MEMBERS } from "../utils/community";
import { fetchReplies, replyToPost, deleteReply, updateReply, reportContent, toggleLike, toggleBookmark } from "../utils/communityApi";
import { blockUser, isBlocked } from "./blocklist";
import { bamConfirm, bamToast } from "../shared/dialog";
import { MemberCard } from "./MemberCard";
// Only loaded when somebody actually reaches for a drill.
import { useAdmin } from "../shared/AdminContext";
import { PostAdminMenu } from "./PostAdminMenu";
import { CertBadge } from "../components/CertBadge";
import { BamCoachBadge } from "../components/BamCoachBadge";
import { InternBadge } from "../components/InternBadge";
import { PostEmbeds } from "./PostEmbeds";
import BamLoading from "../shared/BamLoading";
import { Pop, RollingCount } from "../shared/motion";
const DrillPicker = lazy(() => import("./DrillPicker"));
const FeaturePicker = lazy(() => import("./FeaturePicker"));

// Two-letter initials from any name string. "Härmo Lambut" → "HL".
// Falls back to "?" only when there's nothing to derive — used for the
// reply author bubble when the profile join is missing.
function deriveInitials(name) {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0][0] || "";
  const last  = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0][1] || "");
  return (first + last).toUpperCase() || "?";
}

// Trailing punctuation shouldn't get swallowed into the href — a link at
// the end of a sentence ("...see https://x.com/y.") would otherwise 404.
// Closing brackets are only trimmed when unbalanced, so a URL that
// legitimately ends in ")" survives.
function splitTrailingPunctuation(url) {
  let end = url.length;
  while (end > 0) {
    const ch = url[end - 1];
    if (".,;:!?".includes(ch)) { end -= 1; continue; }
    if (ch === ")" && (url.slice(0, end).match(/\(/g) || []).length < (url.slice(0, end).match(/\)/g) || []).length) { end -= 1; continue; }
    break;
  }
  return [url.slice(0, end), url.slice(end)];
}

// Render body text with @mentions highlighted AND URLs turned into real
// links.
//
// Mentions render in gold: replies are flat (no parent_reply_id in the
// schema), so reply-to-a-reply works Instagram-style — the Reply link
// prefills "@FirstName " and this makes the address visible in the thread.
//
// Replies ran through this helper while post bodies had their own
// inline linkifier, so a coach pasting a link into a COMMENT got dead
// plain text (reported 2026-08-26). One helper now covers both.
//
// Only http/https become links — never javascript:, data: or any other
// scheme, since this text is user-authored.
// `names` are coaches tagged in the post by name (Coleman: "I just want
// their name", no @). They live on the post as {type:"mention"} embeds, so
// only a name that was actually picked from the list is highlighted, never
// a name that merely appears in the text by coincidence of being typed.
const escapeRe = (x) => x.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
function openCoachProfile(name, onProfileClick) {
  if (onProfileClick) { onProfileClick(name); return; }
  // Surfaces that render a PostCard without the handler (the home page's
  // community block) still open the panel: BAMFull listens for this.
  try { window.dispatchEvent(new CustomEvent("bam-open-profile", { detail: { name } })); } catch { /* old browser */ }
}

// A comment author's avatar or name, tappable to open their profile.
// stopPropagation keeps the post card's own click (double-tap to like,
// closing the MemberCard) out of it. `btn-inline` opts out of the global
// 44px min-size rule so the layout doesn't move; the 44px phone target
// comes from the .bam-coach-tap rules in dashboard.css instead. With no
// onOpen (blocked or deleted author) it renders exactly as plain text did.
export function CoachTap({ onOpen, label, className = "", style, children }) {
  if (!onOpen) return <span style={style}>{children}</span>;
  const fire = (e) => { e.preventDefault(); e.stopPropagation(); onOpen(e); };
  return (
    <span role="button" tabIndex={0} aria-label={label}
      className={`btn-inline bam-coach-tap ${className}`.trim()}
      onClick={fire}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") fire(e); }}
      style={{ cursor: "pointer", ...style }}>
      {children}
    </span>
  );
}

function renderWithMentions(text, names = [], onProfileClick) {
  const s = String(text ?? "");
  const tagged = (names || []).filter(n => typeof n === "string" && n.trim()).sort((a, b) => b.length - a.length);
  const nameAlt = tagged.length ? `(${tagged.map(escapeRe).join("|")})|` : "()";
  const re = new RegExp(`${nameAlt}(^|\\s)(@[\\p{L}\\p{N}_.\\-]+)|(https?:\\/\\/[^\\s]+)`, "gu");
  const out = [];
  let last = 0, m;
  // Groups: 1 a tagged name, 2 the space before an @, 3 the @mention, 4 a URL.
  while ((m = re.exec(s)) !== null) {
    if (m[1]) {
      // A coach tagged by name
      const start = m.index;
      if (start > last) out.push(s.slice(last, start));
      const name = m[1];
      out.push(
        <a key={`n${start}`} href="#/community" role="link"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); openCoachProfile(name, onProfileClick); }}
          style={{ color: GOLD, fontWeight: 700, textDecoration: "none", cursor: "pointer" }}>{name}</a>
      );
      last = start + m[1].length;
    } else if (m[3]) {
      // @mention
      const start = m.index + (m[2] || "").length;
      if (start > last) out.push(s.slice(last, start));
      out.push(<span key={`m${start}`} style={{ color: GOLD, fontWeight: 700 }}>{m[3]}</span>);
      last = start + m[3].length;
    } else if (m[4]) {
      // URL
      const start = m.index;
      if (start > last) out.push(s.slice(last, start));
      const [href, trailing] = splitTrailingPunctuation(m[4]);
      out.push(
        <a
          key={`u${start}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); openExternal(href); }}
          style={{ color: GOLD, textDecoration: "underline", wordBreak: "break-all" }}
        >{href}</a>
      );
      if (trailing) out.push(trailing);
      last = start + m[4].length;
    }
  }
  out.push(s.slice(last));
  return out;
}

/**
 * Reply bubble — hoisted to module scope so its useState identity is
 * stable across PostCard re-renders. The previous inline definition
 * inside replies.map() created a fresh function reference every render;
 * React unmounted/remounted the component and the `expanded` state
 * reset to false, producing the visible "Read more" expand-then-close
 * flicker. Lifting the component out fixes that class of bug.
 *
 * Props:
 *   reply       — the reply row { id, userId, content, _pending }
 *   authorName  — display name (prefetched up-tree)
 *   isOwner     — render Edit / Delete affordances when true
 *   C, dark     — theme
 *   onEdit(content), onDelete() — handlers from the parent
 */
// Report + Block menu for content you don't own (Apple Guideline 1.2). Used on
// posts; replies have an equivalent inline menu in CommentBubble.
function ReportMenu({ contentType, contentId, postId, authorUserId, authorName, C, dark, onExport, exporting }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }} aria-label="Post options" className="btn"
        style={{ width: 28, height: 28, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, background: "transparent", border: "none", cursor: "pointer" }}>
        <MoreHorizontal size={15} />
      </button>
      {open && (
        <div className="bam-menu-in" style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, zIndex: 20, background: dark ? "#1a1a1a" : "#fff", border: `1px solid ${C.border}`, borderRadius: 8, minWidth: 150, boxShadow: "0 6px 20px rgba(0,0,0,0.25)", overflow: "hidden" }}>
          {onExport && (
            <button onClick={(e) => { setOpen(false); onExport(e); }}
              style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px", background: "transparent", border: "none", cursor: "pointer", color: C.text, fontSize: 12.5, textAlign: "left", borderBottom: `1px solid ${C.border}` }}>
              <Instagram size={13} /> {exporting ? "Preparing…" : "Share as image"}
            </button>
          )}
          <button onClick={async (e) => { e.stopPropagation(); setOpen(false);
              try { await reportContent({ contentType, contentId, postId: postId || null });
 bamToast("Thanks: this has been reported. We review reports within 24 hours."); }
              catch { bamToast("Couldn't submit the report. Please try again.", { tone: "error" }); } }}
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px", background: "transparent", border: "none", cursor: "pointer", color: C.text, fontSize: 12.5, textAlign: "left" }}>
            <Flag size={13} /> Report
          </button>
          <button onClick={async (e) => { e.stopPropagation(); setOpen(false);
              if (!authorUserId) return;
              if (await bamConfirm({ title: `Block ${authorName || "this user"}?`, message: "You won't see their posts or replies.", confirmLabel: "Block", danger: true })) {
                blockUser(authorUserId);
                bamToast(`${authorName || "User"} blocked.`);
              } }}
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px", background: "transparent", border: "none", cursor: "pointer", color: "#E06060", fontSize: 12.5, textAlign: "left", borderTop: `1px solid ${C.border}` }}>
            <Ban size={13} /> Block user
          </button>
        </div>
      )}
    </div>
  );
}

// Owner's ⋯ menu. Previously the button jumped straight into the delete
// confirmation — a ⋯ affordance that detonates is its own bug. Now it opens
// a menu carrying the Instagram export too, so the action bar stays clean.
function OwnerMenu({ onExport, exporting, onDelete, C, dark }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  return (
    <div ref={ref} style={{ position: "relative", flexShrink: 0 }}>
      <button onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }} aria-label="Post options" className="btn"
        style={{ width: 28, height: 28, borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center", color: C.textDim, background: "transparent", border: "none", cursor: "pointer" }}>
        <MoreHorizontal size={15} />
      </button>
      {open && (
        <div className="bam-menu-in" style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, zIndex: 20, background: dark ? "#1a1a1a" : "#fff", border: `1px solid ${C.border}`, borderRadius: 8, minWidth: 170, boxShadow: "0 6px 20px rgba(0,0,0,0.25)", overflow: "hidden" }}>
          <button onClick={(e) => { setOpen(false); onExport(e); }}
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px", background: "transparent", border: "none", cursor: "pointer", color: C.text, fontSize: 12.5, textAlign: "left" }}>
            <Instagram size={13} /> {exporting ? "Preparing…" : "Share as image"}
          </button>
          <button onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete(); }}
            style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "9px 12px", background: "transparent", border: "none", cursor: "pointer", color: "#E06060", fontSize: 12.5, textAlign: "left", borderTop: `1px solid ${C.border}` }}>
            <Trash2 size={13} /> Delete post
          </button>
        </div>
      )}
    </div>
  );
}

export function CommentBubble({ reply, authorName, isOwner, postId, C, dark, onEdit, onDelete, onReplyTo, onOpenProfile }) {
  const body = reply?.content ?? "";
  const isLong = body.length > 180;
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(body);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const editRef = useRef(null);

  // Close kebab on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDoc = (e) => { if (!menuRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  // Auto-resize textarea to fit content while editing.
  useEffect(() => {
    if (!editing || !editRef.current) return;
    editRef.current.style.height = "0px";
    editRef.current.style.height = Math.min(editRef.current.scrollHeight, 200) + "px";
  }, [editing, draft]);

  const startEdit = () => { setDraft(body); setEditing(true); setMenuOpen(false); };
  const cancelEdit = () => { setEditing(false); setDraft(body); };
  const saveEdit = async () => {
    const next = draft.trim();
    if (!next || next === body) { cancelEdit(); return; }
    setEditing(false);
    await onEdit?.(next);
  };

  return (
    <div style={{
      background: C.bgHover, borderRadius: 9, padding: "9px 13px",
      fontSize: 13, color: C.textMid, lineHeight: 1.6, flex: 1,
      position: "relative", overflow: "visible",  // visible so the kebab popover isn't clipped
      whiteSpace: "pre-wrap", wordBreak: "break-word",
      opacity: reply?._pending ? 0.65 : 1,
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 3 }}>
        {/* Stretched to the row's height (44px on phones, where the kebab
            sets it) so the whole row height is the name's tap target. */}
        <div style={{ fontSize: 11, fontWeight: 700, color: C.text, letterSpacing: .2, display: "flex", alignItems: "center", gap: 5, alignSelf: "stretch" }}>
          <CoachTap onOpen={onOpenProfile} className="bam-coach-name"
            style={{ alignSelf: "stretch", display: "inline-flex", alignItems: "center" }}>{authorName}</CoachTap>
          <InternBadge userId={reply?.userId}/>
        </div>
        {!editing && (
          <div ref={menuRef} style={{ position: "relative" }}>
            <button
              onClick={(e) => { e.stopPropagation(); setMenuOpen(v => !v); }}
              aria-label="Comment options"
              className="btn"
              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 2,
                display: "flex", alignItems: "center", justifyContent: "center",
                color: C.textDim, opacity: 0.7 }}>
              <MoreHorizontal size={14} />
            </button>
            {menuOpen && (
              <div className="bam-menu-in" style={{ position: "absolute", top: "100%", right: 0, marginTop: 4, zIndex: 10,
                background: dark ? "#1a1a1a" : "#fff",
                border: `1px solid ${C.border}`, borderRadius: 8, minWidth: 140,
                boxShadow: "0 6px 20px rgba(0,0,0,0.25)", overflow: "hidden" }}>
                {isOwner ? (
                  <>
                    <button onClick={(e) => { e.stopPropagation(); startEdit(); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%",
                        padding: "8px 12px", background: "transparent", border: "none",
                        cursor: "pointer", color: C.text, fontSize: 12, textAlign: "left" }}>
                      <Pencil size={12} /> Edit
                    </button>
                    <button onClick={async (e) => { e.stopPropagation(); setMenuOpen(false);
                        if (await bamConfirm({ title: "Delete this comment?", message: "This can't be undone. Other coaches will no longer see this reply.", confirmLabel: "Delete", danger: true })) onDelete?.(); }}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%",
                        padding: "8px 12px", background: "transparent", border: "none",
                        cursor: "pointer", color: "#E06060", fontSize: 12, textAlign: "left",
                        borderTop: `1px solid ${C.border}` }}>
                      <Trash2 size={12} /> Delete
                    </button>
                  </>
                ) : (
                  <>
                    <button onClick={async (e) => { e.stopPropagation(); setMenuOpen(false);
                        try { await reportContent({ contentType: "reply", contentId: reply?.id, postId: postId || null });
 bamToast("Thanks: this reply has been reported. We review reports within 24 hours."); }
                        catch { bamToast("Couldn't submit the report. Please try again.", { tone: "error" }); } }}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%",
                        padding: "8px 12px", background: "transparent", border: "none",
                        cursor: "pointer", color: C.text, fontSize: 12, textAlign: "left" }}>
                      <Flag size={12} /> Report
                    </button>
                    <button onClick={async (e) => { e.stopPropagation(); setMenuOpen(false);
                        if (!reply?.userId) return;
                        if (await bamConfirm({ title: `Block ${authorName}?`, message: "You won't see their posts or replies.", confirmLabel: "Block", danger: true })) {
                          blockUser(reply.userId);
                          bamToast(`${authorName} blocked.`);
                        } }}
                      style={{ display: "flex", alignItems: "center", gap: 8, width: "100%",
                        padding: "8px 12px", background: "transparent", border: "none",
                        cursor: "pointer", color: "#E06060", fontSize: 12, textAlign: "left",
                        borderTop: `1px solid ${C.border}` }}>
                      <Ban size={12} /> Block user
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {editing ? (
        <div>
          <textarea
            ref={editRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); saveEdit(); }
              if (e.key === "Escape") { e.preventDefault(); cancelEdit(); }
            }}
            style={{ width: "100%", boxSizing: "border-box", background: dark ? "#0e0e0e" : "#fff",
              color: C.text, border: `1px solid ${GOLD}55`, borderRadius: 8, padding: "8px 10px",
              fontSize: 16, fontFamily: "inherit", lineHeight: 1.5, resize: "none", outline: "none",
              minHeight: 60 }}
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 6 }}>
            <button onClick={cancelEdit} className="btn"
              style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 6,
                padding: "4px 10px", fontSize: 11, color: C.textDim, cursor: "pointer" }}>Cancel</button>
            <button onClick={saveEdit} className="btn"
              style={{ background: GOLD, border: "none", borderRadius: 6, padding: "4px 10px",
                fontSize: 11, fontWeight: 700, color: "#111", cursor: "pointer" }}>Save</button>
          </div>
        </div>
      ) : (
        <>
          {/* Clamp to 3 lines with an ellipsis, then an inline Read more BELOW
              the text — the old absolute overlay sat on top of readable words. */}
          <div style={ !expanded && isLong
            ? { display:"-webkit-box", WebkitLineClamp:3, WebkitBoxOrient:"vertical", overflow:"hidden", whiteSpace:"pre-wrap", wordBreak:"break-word" }
            : { whiteSpace:"pre-wrap", wordBreak:"break-word" } }>
            {renderWithMentions(body)}
          </div>
          {/* Same renderer posts use, so a drill card looks identical whether
              it was posted or replied. */}
          {Array.isArray(reply?.embeds) && reply.embeds.length > 0 && (
            <div style={{ marginTop: 9 }}>
              <PostEmbeds embeds={reply.embeds} C={C} dark={dark}/>
            </div>
          )}
          {(isLong || (onReplyTo && !reply?._pending)) && (
            <div style={{ display:"flex", alignItems:"center", gap:14, marginTop:6 }}>
              {isLong && (
                <span className="btn" onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
                  style={{ fontSize: 12, fontWeight: 700, color: GOLD, cursor: "pointer" }}>
                  {expanded ? "Show less" : "Read more"}
                </span>
              )}
              {onReplyTo && !reply?._pending && (
                <span className="btn" onClick={(e) => { e.stopPropagation(); onReplyTo(authorName); }}
                  title={`Reply to ${authorName}`}
                  style={{ fontSize: 12, fontWeight: 700, color: C.textDim, cursor: "pointer" }}>
                  Reply
                </span>
              )}
            </div>
          )}
        </>
      )}

    </div>
  );
}

// Memoized: the feed lives inside the big app shell, which re-renders on
// composer keystrokes, presence ticks, and banner countdowns — without memo,
// every one of those re-rendered every post. Callers must pass identity-
// stable props (see BAMFull meForPost/onPost*).
export const PostCard = React.memo(function PostCard({p,C,dark,compact,me,onTagClick,activeTag,onProfileClick,showLikeHint,onDelete,onAdminChanged,initiallyLiked=false,initiallyBookmarked=false}){
  const { isAdmin, adminMode, setOpenUser } = useAdmin();
  const openUserPanel = (e) => {
    if (!isAdmin || !adminMode || !p.userId) return false;
    e?.stopPropagation();
    setOpenUser({ id: p.userId, name: p.author, avatarUrl: p.authorAvatar });
    return true;
  };
  const [lk,setLk]=useState(initiallyLiked);
  const [open,setOpen]=useState(false);
  // The replies accordion animates 0fr→1fr with overflow:hidden. Once the
  // open transition has finished we drop to auto height + visible overflow:
  // on iOS the hidden-overflow track was clipping the tail of long threads
  // and the page would not scroll to the last reply.
  const [settled,setSettled]=useState(false);
  useEffect(()=>{
    if(!open){ setSettled(false); return; }
    const t=setTimeout(()=>setSettled(true),380);
    return ()=>clearTimeout(t);
  },[open]);
  const [bk,setBk]=useState(initiallyBookmarked);
  // Re-sync when hydration arrives after first render (fetchMyLikes/
  // fetchMyBookmarks resolve alongside the feed, so the props start false
  // and flip once loaded).
  useEffect(()=>{ setLk(initiallyLiked); },[initiallyLiked]);
  useEffect(()=>{ setBk(initiallyBookmarked); },[initiallyBookmarked]);
  // The server likes count already includes my own like when
  // initiallyLiked — don't double-count it on top of local state.
  const likeDelta=(lk?1:0)-(initiallyLiked?1:0);
  const [bodyExpanded,setBodyExpanded]=useState(false);
  const [replyText,setReply]=useState("");
  // A drill attached to the comment being written. Array to match the post
  // shape, capped at one because a comment pointing at three drills is a post.
  const [replyEmbeds,setReplyEmbeds]=useState([]);
  const [drillPickerOpen,setDrillPickerOpen]=useState(false);
  const [featurePickerOpen,setFeaturePickerOpen]=useState(false);
  // Replies are loaded on demand when the user opens the accordion.
  // The feed query only returns a placeholder array sized by reply_count
  // (see communityApi.fetchCommunityPosts) — we use that for the count
  // badge until real replies are fetched.
  const [replies,setReplies]=useState([]);
  const [repliesLoaded,setRepliesLoaded]=useState(false);
  const [replyBusy,setReplyBusy]=useState(false);
  const [replyError,setReplyError]=useState("");
  // Threaded-reply target: {parentId, userId, name} when replying to a
  // specific comment; null = replying to the post itself.
  const [replyTarget,setReplyTarget]=useState(null);
  const initialReplyCount = Array.isArray(p.replies) ? p.replies.length : 0;
  const replyCount = repliesLoaded ? replies.length : initialReplyCount;
  const [memberOpen,setMemberOpen]=useState(false);
  const [lastTap,setLastTap]=useState(0);
  const [hintVis,setHintVis]=useState(!!showLikeHint);
  const [deleted,setDeleted]=useState(false);
  const [exportImg,setExportImg]=useState(null);
  const [exporting,setExporting]=useState(false);
  const doExport = async (e) => {
    e.stopPropagation();
    if (exporting) return;
    bamHaptic("light"); setExporting(true);
    try {
      const img = await renderPostCard({ author: p.author || "Coach", body: p.content || "", handle: p.authorHandle || "" });
      setExportImg(img);
    } catch (err) { console.error("[export] failed", err); }
    finally { setExporting(false); }
  };
  useEffect(()=>{if(showLikeHint){const t=setTimeout(()=>setHintVis(false),3000);return()=>clearTimeout(t);}},[showLikeHint]);
  // Delete confirm via bamConfirm — its own React root on <body>, fully
  // outside this card's event tree. The old inline modal lived INSIDE the
  // tappable card (double-tap handler, menu outside-click listeners), and a
  // coach reported it re-opening after Cancel on iOS; a detached dialog with
  // backdrop + Escape dismissal can't be re-triggered by card events.
  const askDeletePost = async () => {
    const ok = await bamConfirm({ title: "Delete this post?", message: "All comments will also be removed.", confirmLabel: "Delete", danger: true });
    if (!ok) return;
    setDeleted(true);
    onDelete && onDelete(p.id);
  };

  // Fetch real replies the first time the accordion is opened. Skipped
  // when the post genuinely has zero replies — no point round-tripping.
  useEffect(() => {
    if (!open || repliesLoaded || initialReplyCount === 0) return;
    let cancelled = false;
    (async () => {
      const data = await fetchReplies(p.id);
      if (cancelled) return;
      setReplies(data || []);
      setRepliesLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [open, repliesLoaded, initialReplyCount, p.id]);
  const nameRef=useRef(null);
  const replyRef=useRef(null);
  // Opening a post with no replies means they came to write, not read —
  // put the cursor in the composer. With replies, don't steal the keyboard.
  useEffect(() => {
    if (open && initialReplyCount === 0) replyRef.current?.focus();
  }, [open, initialReplyCount]);
  const ac=getAC(p.author);
  const memberAv=MEMBERS[p.author]||p.authorMeta||{};

  // Optimistic like/bookmark — flip immediately, persist in the background,
  // roll back on failure. An in-flight ref stops rapid taps from racing the
  // read-then-write toggle on the server.
  const likeBusy=useRef(false);
  const like=()=>{
    if(likeBusy.current) return;
    likeBusy.current=true;
    const prev=lk;
    setLk(!prev); bamHaptic("light");
    // The server's answer is authoritative — if the heart state had drifted
    // from the DB (stale feed, second device), snap to what actually stuck.
    toggleLike(p.id)
      .then(liked=>{ setLk(liked); })
      .catch(e=>{
        console.error("toggleLike failed:",e);
        setLk(prev);
 bamToast("Couldn't save your like: check your connection and try again.", { tone: "error" });
      })
      .finally(()=>{ likeBusy.current=false; });
  };
  const bkBusy=useRef(false);
  const bookmark=()=>{
    if(bkBusy.current) return;
    bkBusy.current=true;
    const prev=bk;
    setBk(!prev); bamHaptic("light");
    toggleBookmark(p.id)
      .then(saved=>{ setBk(saved); })
      .catch(e=>{
        console.error("toggleBookmark failed:",e);
        setBk(prev);
 bamToast("Couldn't save your bookmark: try again.", { tone: "error" });
      })
      .finally(()=>{ bkBusy.current=false; });
  };
  const adjustReplyHeight=()=>{
    const el=replyRef.current;
    if(!el) return;
    el.style.height="0px";
    el.style.height=Math.min(el.scrollHeight,120)+"px";
  };
  const submitReply=async ()=>{
    const content = replyText.trim();
    // A drill on its own is a complete answer to "which one do you mean?"
    if((!content && replyEmbeds.length === 0) || replyBusy) return;
    setReplyBusy(true);
    // Optimistic insert so the bubble appears immediately. On success
    // we replace the temp row with the server-returned reply (real id +
    // timestamp). On failure we roll back and keep the text in the
    // composer so the coach doesn't lose their thought.
    const target = replyTarget;
    const tempId = `temp-${Date.now()}`;
    const attached = replyEmbeds;
    const optimistic = {
      id: tempId,
      // userId on the optimistic row so the owner's own freshly-posted
      // reply renders the edit/delete kebab without waiting for a refetch.
      userId: me?.id || null,
      content,
      embeds: attached,
      author: me?.name || "You",
      authorAvatar: me?.photo || null,
      createdAt: new Date().toISOString(),
      parentId: target?.parentId || null,
      _pending: true,
    };
    setReplies(r => [...r, optimistic]);
    setRepliesLoaded(true);
    setReply("");
    setReplyEmbeds([]);
    setReplyTarget(null);
    if(replyRef.current) replyRef.current.style.height="";
    setOpen(true);
    try {
      const saved = await replyToPost(p.id, content, {
        parentReplyId: target?.parentId || null,
        targetUserId: target?.userId || null,
        embeds: attached,
      });
      setReplies(r => r.map(x => x.id === tempId ? {
        id: saved.id,
        userId: me?.id || null,
        content: saved.content,
        embeds: attached,
        author: optimistic.author,
        authorAvatar: optimistic.authorAvatar,
        createdAt: saved.created_at,
        parentId: saved.parent_reply_id ?? (target?.parentId || null),
      } : x));
    } catch (err) {
      console.error("replyToPost failed:", err);
      setReplies(r => r.filter(x => x.id !== tempId));
      setReply(content);
      setReplyTarget(target);
      // Surface the failure — the bubble vanishing with the text back in the
      // box otherwise reads as a glitch, not a failed send.
 setReplyError("Couldn't post your reply: check your connection and try again.");
      setTimeout(() => setReplyError(""), 5000);
    } finally {
      setReplyBusy(false);
    }
  };
  const tapCount=useRef(0);
  const tapTimer=useRef(null);

  const handleDoubleTap=()=>{
    tapCount.current++;
    if(tapTimer.current) clearTimeout(tapTimer.current);
    tapTimer.current=setTimeout(()=>{tapCount.current=0;},400);
    if(tapCount.current===2){ if(!lk){like();} tapCount.current=0; }
    else if(tapCount.current>=3){ tapCount.current=0; }
  };

  const fmtLikes=(n)=>{if(n>=10000) return Math.floor(n/1000)+"k"; if(n>=1000){const k=n/1000;return k%1===0?k+"k":k.toFixed(1)+"k";} return n;};

  if(deleted) return null;

  // Photo source priority:
  //   1. MEMBERS map (legacy/seeded contacts have a hand-curated photo)
  //   2. p.authorAvatar (set by communityApi from profiles.avatar_url)
  //   3. p.authorMeta.avatar_url (legacy nested shape)
  // Initials fall back to MEMBERS, then a name-derived two-letter token.
  const photoUrl = memberAv?.photo || p.authorAvatar || p.authorMeta?.avatar_url || null;
  const fallbackInitials = memberAv?.initials || (
    (p.author || "")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map(s => s[0])
      .join("")
      .toUpperCase()
  ) || "?";

  const avatar = (
    <div className="btn" onClick={e=>{
      if (openUserPanel(e)) return;
      e.stopPropagation();
      onProfileClick&&onProfileClick(p.author);
    }}
      style={{position:"relative",width:40,height:40,borderRadius:"50%",background:ac,display:"flex",alignItems:"center",
        justifyContent:"center",fontSize:13,fontWeight:800,color:"#fff",flexShrink:0,
        boxShadow:`0 2px 8px ${ac}55`,overflow:"hidden"}}>
      {/* Initials sit behind the photo, so a broken/404 avatar reveals them
          instead of the browser's broken-image glyph. */}
      <span style={{position:"absolute",inset:0,display:"flex",alignItems:"center",justifyContent:"center"}}>{fallbackInitials}</span>
      {photoUrl &&
        <img src={photoUrl} onError={e=>{e.currentTarget.style.display="none";}}
          style={{position:"relative",width:"100%",height:"100%",objectFit:"cover"}} alt=""/>}
    </div>
  );

  return (
    <div className="pc" onClick={()=>{ if(memberOpen) setMemberOpen(false); handleDoubleTap(); }}
      style={{background:C.cardGradient,border:`1px solid ${C.cardBorder}`,
        borderRadius:16,padding:compact?"14px 16px":"22px 24px",marginBottom:16,
        boxShadow:`${C.surfaceShadow}, ${C.innerGlow}`,"--hs":C.elevatedShadow,"--hb":C.cardHoverBorder,
        cursor:"default",position:"relative"}}>
      {memberOpen&&<MemberCard name={p.author} anchorRef={nameRef} C={C} dark={dark} onClose={()=>setMemberOpen(false)}/>}
      {/* Card-level so header-menu exports work on compact cards too */}
      {exportImg && <ExportPostModal image={exportImg} onClose={()=>{ try{URL.revokeObjectURL(exportImg.url);}catch{} setExportImg(null); }}/>}

      {/* Author row */}
      <div style={{display:"flex",gap:12,alignItems:"flex-start",marginBottom:10}}>
        {avatar}
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
            <span ref={nameRef} className="btn" onClick={e=>{
              if (openUserPanel(e)) return;
              e.stopPropagation();
              onProfileClick?onProfileClick(p.author):setMemberOpen(v=>!v);
            }} style={{fontSize:15,fontWeight:700,color:C.text}}>{p.author}</span>
            {/* BAM Coach pill takes precedence — staff don't double-up
                with cert badge. Cert renders only when not staff. */}
            {p.authorIsBamCoach
              ? <BamCoachBadge size="sm"/>
              : <CertBadge status={p.authorCertStatus} size="sm"/>}
            <InternBadge userId={p.userId}/>
            {p.time && <span style={{fontSize:12,color:C.textDim,fontWeight:500,flexShrink:0,whiteSpace:"nowrap"}}>· {p.time}</span>}
            {p.pinnedAt && <span title="Pinned by admin" style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10,fontWeight:800,color:GOLD,background:`${GOLD}18`,padding:"2px 7px",borderRadius:8,border:`1px solid ${GOLD}55`,letterSpacing:.5}}><Pin size={9} color={GOLD}/> PINNED</span>}
            {p.hiddenAt && <span title="Hidden (admin only)" style={{display:"inline-flex",alignItems:"center",gap:3,fontSize:10,fontWeight:800,color:"#F87171",background:"rgba(248,113,113,0.12)",padding:"2px 7px",borderRadius:8,border:"1px solid rgba(248,113,113,0.35)",letterSpacing:.5}}><EyeOff size={9} color="#F87171"/> HIDDEN</span>}
            {memberAv?.ig&&<span className="btn" style={{display:"flex"}}><Ic.IG c={C.textDim}/></span>}
            {memberAv?.tw&&<span className="btn" style={{display:"flex"}}><Ic.TW c={C.textDim}/></span>}
          </div>
          {/* Location line only when there IS a location — real users have no
              MEMBERS entry, and the old unconditional render left a bare
              "· 1d" orphaned under the name. Time now lives in the name row. */}
          {(memberAv?.loc || memberAv?.flag) && (
            <div style={{fontSize:12,color:C.textDim,display:"flex",alignItems:"center",gap:4}}>
              {memberAv?.flag && <span style={{fontSize:13}}>{memberAv.flag}</span>}
              {memberAv?.loc}
            </div>
          )}
        </div>
        {/* An Intro (Start Here's first post) wears no label: it's tagged so
            the admin can find it, not so members see a category. */}
        {p.tag!=="Intro"&&<div className="tag-pill" onClick={e=>{e.stopPropagation();onTagClick&&onTagClick(p.tag);}} style={{fontSize:11,fontWeight:700,color:"#111",background:GOLD,padding:"4px 11px",borderRadius:20,flexShrink:0,opacity:activeTag&&activeTag!==p.tag?0.4:1}}>{p.tag}</div>}
        <PostAdminMenu post={p} C={C} dark={dark} onChanged={onAdminChanged}/>
        {onDelete&&!(isAdmin&&adminMode)&&<OwnerMenu onExport={doExport} exporting={exporting} onDelete={askDeletePost} C={C} dark={dark}/>}
        {/* Report / Block for posts you don't own (Apple 1.2 UGC requirement). */}
        {!onDelete && !(isAdmin&&adminMode) && me?.id && p.userId && p.userId !== me.id && (
          <ReportMenu contentType="post" contentId={p.id} authorUserId={p.userId} authorName={p.author} C={C} dark={dark} onExport={doExport} exporting={exporting}/>
        )}
      </div>

      {/* Title */}
      {p.title&&<div style={{fontSize:16,fontWeight:800,color:C.text,marginBottom:6,lineHeight:1.35}}>{p.title}</div>}

      {/* Body — `whiteSpace: pre-wrap` preserves the line breaks the
          user typed in the composer (so "paragraph 1 \n\n paragraph 2"
          actually renders as two paragraphs instead of one collapsed
          block). `wordBreak: break-word` keeps long pasted URLs / hashes
          from busting the card width.
          Truncation: long bodies clip to ~3-4 lines with a fade
          gradient + Read more chip, mirroring the comment-bubble
          pattern. Threshold uses character count rather than maxHeight
          so a few short lines don't trip the gradient. */}
      {(() => {
        const text = p.content || "";
        const TRUNC_AT = 460;
        const isLong = text.length > TRUNC_AT && !compact;
        const collapsed = isLong && !bodyExpanded;
        const visibleText = compact ? text.slice(0,105)+"..." : (collapsed ? text.slice(0, TRUNC_AT).replace(/\s+\S*$/, "") + "…" : text);
        return (
          <div style={{position:"relative",marginBottom:12}}>
            <div style={{fontSize:15,color:C.textMid,lineHeight:1.75,whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
              {renderWithMentions(visibleText, (Array.isArray(p.embeds) ? p.embeds : []).filter(e => e?.type === "mention").map(e => e.name), onProfileClick)}
            </div>
            {isLong && (
              <span className="btn"
                onClick={e=>{e.stopPropagation();setBodyExpanded(v=>!v);}}
                style={{display:"inline-block",fontSize:12,fontWeight:700,color:GOLD,cursor:"pointer",marginTop:10}}>
                {collapsed ? "Read more" : "Show less"}
              </span>
            )}
          </div>
        );
      })()}

      {/* Rich embeds (film clips, snapshots, voice notes attached to
          posts via the share-from-Film-Studio flow). Self-contained
          payloads — no follow-up fetch. */}
      {!compact && Array.isArray(p.embeds) && p.embeds.some(e => e?.type !== "mention") && (
        <PostEmbeds embeds={p.embeds.filter(e => e?.type !== "mention")} C={C} dark={dark}/>
      )}

      {/* Inline media */}
      {!compact&&p.media&&p.media.length>0&&(
        <div style={{marginBottom:14,display:"flex",gap:8,flexDirection:"column"}}>
          {p.media.map((m,mi)=>(
            <div key={mi} style={{borderRadius:12,overflow:"hidden",border:`1px solid ${C.border}`}}>
              {m.type==="image"&&<img src={m.url} alt={m.name} loading="lazy" onError={e=>{e.currentTarget.style.display="none";}} onLoad={e=>{e.currentTarget.style.opacity=1;}} style={{width:"100%",aspectRatio:"4 / 3",maxHeight:340,objectFit:"cover",display:"block",opacity:0,transition:"opacity .3s ease",background:"rgba(255,255,255,0.04)"}}/>}
              {m.type==="video"&&<video src={m.url} controls playsInline controlsList="nodownload" style={{width:"100%",maxHeight:340,background:"#000",display:"block"}}/>}
              {m.type==="pdf"&&(
                <div style={{display:"flex",alignItems:"center",gap:12,padding:"13px 16px",background:dark?"#1E1510":"#FFF8F5"}}>
                  <div style={{width:38,height:46,borderRadius:7,background:dark?"#3A1F10":"#FFE8DE",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0}}>&#x1F4C4;</div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name}</div>
                    {m.size&&<div style={{fontSize:11,color:C.textDim,marginTop:2}}>{m.size}</div>}
                  </div>
                  <div style={{fontSize:11,fontWeight:700,color:GOLD,background:dark?"rgba(226,221,159,0.1)":"rgba(226,221,159,0.25)",padding:"5px 12px",borderRadius:7,border:`1px solid ${GOLD}55`,flexShrink:0}}>View PDF</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Double-tap heart flash */}
      {/* The 44px heart that flew up the middle of the card on a like is gone; the heart in the action row does the work. */}

      {!compact&&(
        <>
          {/* Reactor preview row — renders avatar trio + count when the
              parent passes p.likedBy / p.replyAuthors arrays (shape:
              [{name, initials, avatarUrl}]). Until the schema lands,
              degrades to a quiet text-only count. */}
          {(() => {
            const likers = Array.isArray(p.likedBy) ? p.likedBy : [];
            const commenters = Array.isArray(p.replyAuthors) ? p.replyAuthors : [];
            const totalLikes = Math.max(0, p.likes + likeDelta);
            const totalReplies = replies.length;
            if (totalLikes === 0 && totalReplies === 0) return null;
            const AvatarStack = ({people}) => (
              <div style={{display:"inline-flex"}}>
                {people.slice(0,3).map((person,i)=>{
                  const pac = getAC(person.name || "");
                  return (
                    <div key={i} title={person.name||""} style={{
                      width:18,height:18,borderRadius:"50%",
                      background:person.avatarUrl?"transparent":pac,
                      border:`1.5px solid ${dark?"#0E0E0E":"#FFF"}`,
                      marginLeft:i===0?0:-6,
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:8,fontWeight:800,color:"#fff",overflow:"hidden",flexShrink:0,zIndex:3-i}}>
                      {person.avatarUrl
                        ? <img src={person.avatarUrl} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
                        : <span>{person.initials || (person.name||"?")[0]}</span>}
                    </div>
                  );
                })}
              </div>
            );
            return (
              <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:10,fontSize:11.5,color:C.textDim}}>
                {totalLikes > 0 && (
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    {likers.length > 0 && <AvatarStack people={likers}/>}
                    <span>
                      {likers.length > 0
                        ? <>Liked by <span style={{color:C.textMid,fontWeight:600}}>{likers[0].name?.split(" ")[0]}</span>{likers.length > 1 ? <> and <span style={{color:C.textMid,fontWeight:600}}>{totalLikes - 1} {totalLikes - 1 === 1 ? "other" : "others"}</span></> : null}</>
                        : <>{fmtLikes(totalLikes)} {totalLikes === 1 ? "like" : "likes"}</>}
                    </span>
                  </div>
                )}
                {totalReplies > 0 && (
                  <div style={{display:"flex",alignItems:"center",gap:6}}>
                    {commenters.length > 0 && <AvatarStack people={commenters}/>}
                    <span>{totalReplies} {totalReplies === 1 ? "reply" : "replies"}</span>
                  </div>
                )}
              </div>
            );
          })()}

          {/* Latest comment preview — shows the most recent reply inline so
              coaches can taste the conversation without expanding it. Tap
              to open the full accordion. Only renders while the thread is
              collapsed; once open, the real reply list above takes over. */}
          {!open && p.lastReply && (
            <div onClick={e=>{e.stopPropagation();setOpen(true);}}
              className="post-action"
              style={{display:"flex",gap:10,alignItems:"flex-start",padding:"10px 12px",margin:"0 0 10px",
                background:C.bgHover,border:`1px solid ${C.border}`,borderRadius:10,cursor:"pointer"}}>
              {p.lastReply.authorAvatar ? (
                <img src={p.lastReply.authorAvatar} alt={p.lastReply.author}
                  style={{width:24,height:24,borderRadius:"50%",objectFit:"cover",border:`1px solid ${C.border}`,flexShrink:0}}/>
              ) : (
                <div style={{width:24,height:24,borderRadius:"50%",background:dark?"#1a1a1a":"#EFEFEF",border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:700,color:C.textDim,flexShrink:0}}>{deriveInitials(p.lastReply.author)}</div>
              )}
              <div style={{flex:1,minWidth:0,fontSize:13,lineHeight:1.45,color:C.textMid}}>
                <span style={{fontWeight:700,color:C.text}}>{p.lastReply.author}</span>
                <span style={{color:C.textDim}}> · </span>
                <span>{p.lastReply.content.length > 140 ? p.lastReply.content.slice(0, 140).trim() + "…" : p.lastReply.content}</span>
                {replyCount > 1 && (
                  <div style={{marginTop:4,fontSize:12,color:GOLD,fontWeight:600}}>View {replyCount - 1} more {replyCount - 1 === 1 ? "reply" : "replies"}</div>
                )}
              </div>
            </div>
          )}

          {/* Action bar */}
          <div style={{display:"flex",alignItems:"center",gap:2,paddingTop:12,borderTop:`1px solid ${C.border}`}}>
            <span onClick={e=>{e.stopPropagation();like();}} className="post-action lbtn"
              style={{position:"relative",display:"flex",alignItems:"center",gap:6,fontSize:13,color:lk?GOLD:C.textDim,
                fontWeight:lk?700:400,userSelect:"none",
                background:lk?(dark?"rgba(226,221,159,0.1)":"rgba(226,221,159,0.15)"):"transparent"}}>
              <Pop on={lk}><Ic.Heart c={lk?GOLD:C.textDim} f={lk} s={15}/></Pop><RollingCount value={Math.max(0, p.likes + likeDelta)} format={fmtLikes}/>
              {hintVis&&<div style={{position:"absolute",top:"100%",left:0,fontSize:10,color:C.textDim,opacity:0.45,fontStyle:"italic",whiteSpace:"nowrap",marginTop:2}}>double-tap to like</div>}
            </span>
            <span className="post-action" onClick={e=>{e.stopPropagation();setOpen(v=>!v);}}
              title={replyCount===0?"No replies yet":(open?"Hide replies":"Show replies")}
              style={{display:"flex",alignItems:"center",gap:6,fontSize:13,color:open?GOLD:C.textDim,
                fontWeight:open?700:400,
                background:open?(dark?"rgba(226,221,159,0.1)":"rgba(226,221,159,0.15)"):"transparent"}}>
              <Ic.Msg c={open?GOLD:C.textDim} s={13}/>{replyCount}
            </span>
            <div style={{flex:1}}/>
            <span className="post-action lbtn"
              onClick={e=>{e.stopPropagation();bookmark();}}
              style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:bk?GOLD:C.textDim,fontWeight:bk?700:400,
                background:bk?(dark?"rgba(226,221,159,0.08)":"rgba(226,221,159,0.12)"):"transparent"}}>
              <Pop on={bk}><Bookmark size={14} color={bk?GOLD:C.textDim} fill={bk?GOLD:"none"}/></Pop>{bk?"Saved":"Save"}
            </span>
            <span className="post-action" onClick={async e=>{
              e.stopPropagation();
              // Native iOS / Android share sheet (falls back to Web Share
              // API on Safari, then clipboard-copy on desktop).
              const url = `${window.location.origin}/#/post/${p.id}`;
              await bamNativeShare({
                title: `${p.author || "Coach"} on BAM`,
                text: (p.content || "").slice(0, 200),
                url,
                dialogTitle: "Share post",
              });
            }}
              style={{display:"flex",alignItems:"center",gap:5,fontSize:12,color:C.textDim,cursor:"pointer"}}>
              <Share2 size={14} color={C.textDim}/>
            </span>
          </div>

          {/* Existing replies — accordion (toggled by the comment icon,
              which also reveals the composer below). Animated via
              grid-template-rows 0fr→1fr, which reaches TRUE auto height —
              the old maxHeight:600px hack silently clipped any thread
              taller than 600px (one expanded long reply was enough) with
              no way to scroll to the rest. */}
          <div style={{
            display:"grid",
            gridTemplateRows:open && replyCount>0 ? (settled ? "auto" : "1fr") : "0fr",
            transition:settled ? "none" : "grid-template-rows 0.35s cubic-bezier(0.4,0,0.2,1)",
          }}>
           <div style={{overflow:settled && open ? "visible" : "hidden",minHeight:0}}>
            <div style={{paddingTop:14}}>
              {open && !repliesLoaded && initialReplyCount>0 && (
                <BamLoading pad={10} size={16} label="Loading replies…"/>
              )}
              {(() => {
                // One-level thread: top-level replies with their children
                // indented beneath. Orphans (parent hidden/blocked/deleted)
                // fall back to top level rather than disappearing.
                const visible = replies.filter(r => !isBlocked(r?.userId));
                const ids = new Set(visible.map(r => r?.id));
                const topLevel = visible.filter(r => !r?.parentId || !ids.has(r.parentId));
                const kidsOf = {};
                visible.forEach(r => {
                  if (r?.parentId && ids.has(r.parentId) && !topLevel.includes(r)) {
                    (kidsOf[r.parentId] = kidsOf[r.parentId] || []).push(r);
                  }
                });
                const renderRow = (r, isChild) => {
                  const authorName = r?.author || "Coach";
                  const authorInitials = deriveInitials(authorName);
                  const authorAvatar = r?.authorAvatar || null;
                  const isOwner = !!(me?.id && r?.userId && r.userId === me.id);
                  const av = isChild ? 22 : 28;
                  // Same route as the post author: admin mode opens the admin
                  // user panel, otherwise the coach's ProfilePanel (via
                  // onProfileClick, or the bam-open-profile event on surfaces
                  // without it). Blocked authors are filtered out above; an
                  // author with no account behind them (deleted, or no
                  // name to look up) stays plain text.
                  const onOpenProfile = (r?.userId && r?.author && !isBlocked(r.userId)) ? () => {
                    if (isAdmin && adminMode) { setOpenUser({ id: r.userId, name: authorName, avatarUrl: authorAvatar }); return; }
                    openCoachProfile(authorName, onProfileClick);
                  } : null;
                  const onEditReply = async (newContent) => {
                    const trimmed = (newContent || "").trim();
                    if (!trimmed) return;
                    // Optimistic update so the bubble reflects immediately.
                    setReplies(rs => rs.map(x => x.id === r.id ? { ...x, content: trimmed } : x));
                    try { await updateReply(r.id, trimmed); }
                    catch (e) { console.error("updateReply failed:", e); }
                  };
                  const onDeleteReply = async () => {
                    // Optimistic remove.
                    setReplies(rs => rs.filter(x => x.id !== r.id));
                    try { await deleteReply(r.id); }
                    catch (e) {
                      console.error("deleteReply failed:", e);
                      // Roll back on failure so the row reappears.
                      setReplies(rs => [...rs, r].sort((a, b) =>
                        new Date(a.createdAt || 0) - new Date(b.createdAt || 0)));
                    }
                  };
                  return (
                    <div key={r?.id} style={{display:"flex",gap:10,marginBottom:10,alignItems:"flex-start",marginLeft:isChild?38:0}}>
                      <CoachTap onOpen={onOpenProfile} label={authorName} className="bam-coach-av"
                        style={{display:"flex",flexShrink:0,borderRadius:"50%"}}>
                        {authorAvatar ? (
                          <img src={authorAvatar} alt={authorName}
                            style={{width:av,height:av,borderRadius:"50%",objectFit:"cover",border:`1px solid ${C.border}`,flexShrink:0}}/>
                        ) : (
                          <div style={{width:av,height:av,borderRadius:"50%",background:C.bgHover,border:`1px solid ${C.border}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:isChild?8:9,fontWeight:700,color:C.textDim,flexShrink:0}}>{authorInitials}</div>
                        )}
                      </CoachTap>
                      <CommentBubble
                        onOpenProfile={onOpenProfile}
                        reply={r}
                        authorName={authorName}
                        isOwner={isOwner}
                        postId={p.id}
                        C={C}
                        dark={dark}
                        onEdit={onEditReply}
                        onDelete={onDeleteReply}
                        onReplyTo={(name)=>{
                          // Threaded reply: attach to this comment's top-level
                          // parent (one level deep), notify the comment's
                          // author, and prefill "@FirstName " for context.
                          const first = String(name||"").trim().split(/\s+/)[0];
                          setReplyTarget({
                            parentId: isChild ? r.parentId : r.id,
                            userId: r?.userId || null,
                            name: name || "Coach",
                          });
                          if (first) {
                            const tag = `@${first} `;
                            setReply(prev => prev.startsWith(tag) ? prev : tag + prev);
                          }
                          setTimeout(()=>{ try{ replyRef.current?.focus(); adjustReplyHeight(); }catch{} },0);
                        }}
                      />
                    </div>
                  );
                };
                return topLevel.map(r => (
                  <React.Fragment key={r?.id}>
                    {renderRow(r, false)}
                    {(kidsOf[r?.id] || []).map(c => renderRow(c, true))}
                  </React.Fragment>
                ));
              })()}
            </div>
           </div>
          </div>

          {/* Reply composer — opens with the comment icon. It used to be
              always-visible under every post, which cut the feed to roughly
              one post per screen; collapsing it is the single biggest
              density win in the feed. */}
          {open && replyTarget && (
            <div style={{display:"flex",alignItems:"center",gap:8,marginTop:8,marginLeft:38,fontSize:11.5,color:C.textDim}}>
              <span>Replying to <b style={{color:GOLD}}>{replyTarget.name}</b></span>
              <span className="btn" onClick={()=>setReplyTarget(null)} role="button" aria-label="Cancel reply target"
                style={{cursor:"pointer",fontWeight:800,color:C.textDim,padding:"0 4px"}}>✕</span>
            </div>
          )}
          {open && (
          <div className="reply-composer"
            style={{display:"flex",gap:10,alignItems:"center",marginTop:replyTarget ? 6 : replyCount>0 ? 8 : 14}}>
            {me?.photo ? (
              <img src={me.photo} alt={me?.name || "You"}
                style={{width:28,height:28,borderRadius:"50%",objectFit:"cover",flexShrink:0,alignSelf:"flex-start",marginTop:3}}/>
            ) : (
              <div style={{width:28,height:28,borderRadius:"50%",background:GOLD,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:"#1A1A1A",flexShrink:0,alignSelf:"flex-start",marginTop:3}}>{deriveInitials(me?.name)}</div>
            )}
            <div style={{flex:1,display:"flex",alignItems:"flex-end",gap:10,background:C.bgHover,borderRadius:10,padding:"8px 10px 8px 13px",border:`1px solid ${C.border}`}}>
              <textarea
                ref={replyRef}
                rows={1}
                value={replyText}
                onChange={e=>{setReply(e.target.value);adjustReplyHeight();}}
                onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();submitReply();}}}
                onClick={e=>e.stopPropagation()}
                disabled={replyBusy}
                placeholder={replyCount===0 ? "Be the first to reply…" : "Add a reply…"}
                style={{flex:1,background:"transparent",border:"none",outline:"none",boxShadow:"none",WebkitAppearance:"none",appearance:"none",borderRadius:0,fontSize:16,color:C.text,fontFamily:"'DM Sans',sans-serif",resize:"none",lineHeight:1.35,minHeight:22,maxHeight:120,overflowY:"auto",padding:0,margin:0,display:"block"}}
              />
              <span className="btn btn-inline" title="Attach a drill" aria-label="Attach a drill"
                onClick={e=>{e.stopPropagation();setDrillPickerOpen(true);}}
                style={{flexShrink:0,alignSelf:"flex-start",height:22,padding:"8px 6px",margin:"-8px -6px",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:0,color:replyEmbeds[0]?.type==="drill"?GOLD:C.textDim,cursor:"pointer"}}>
                <Film size={15}/>
              </span>
              <span className="btn btn-inline" title="Link a page" aria-label="Link a page"
                onClick={e=>{e.stopPropagation();setFeaturePickerOpen(true);}}
                style={{flexShrink:0,alignSelf:"flex-start",height:22,padding:"8px 6px",margin:"-8px -6px",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:0,color:replyEmbeds[0]?.type==="feature"?GOLD:C.textDim,cursor:"pointer"}}>
                <Link2 size={15}/>
              </span>
              {(() => {
                const ready = (replyText.trim() || replyEmbeds.length > 0) && !replyBusy;
                return (
                  <span className={"btn btn-inline"+(ready?" reply-send-pulse":"")}
                    onClick={e=>{e.stopPropagation();submitReply();}}
                    style={{opacity:ready?1:0.4,pointerEvents:ready?"auto":"none",flexShrink:0,alignSelf:"flex-start",height:22,padding:"8px 6px",margin:"-8px -6px",display:"flex",alignItems:"center",justifyContent:"center",lineHeight:0}}>
                    <Ic.Send c={ready?GOLD:C.textDim} s={15}/>
                  </span>
                );
              })()}
            </div>
            {replyError && (
              <div style={{marginTop:8,fontSize:12,color:"#E06060",lineHeight:1.4}}>{replyError}</div>
            )}
          </div>
          )}
          {open && replyEmbeds.length > 0 && (
            <div style={{marginTop:8,marginLeft:38,display:"flex",alignItems:"center",gap:8,
              padding:"7px 10px",borderRadius:9,border:`1px solid ${GOLD}44`,background:`${GOLD}0d`}}>
              {replyEmbeds[0].type==="feature" ? <Link2 size={13} color={GOLD} style={{flexShrink:0}}/> : <Film size={13} color={GOLD} style={{flexShrink:0}}/>}
              <span style={{flex:1,minWidth:0,fontSize:12.5,color:C.text,whiteSpace:"nowrap",
                overflow:"hidden",textOverflow:"ellipsis"}}>{replyEmbeds[0].title}</span>
              <button className="btn" onClick={()=>setReplyEmbeds([])} aria-label="Remove attachment"
                style={{background:"transparent",border:"none",color:C.textDim,cursor:"pointer",padding:2,lineHeight:0}}>
                <XIcon size={12}/>
              </button>
            </div>
          )}
          {drillPickerOpen && (
            <Suspense fallback={null}>
              <DrillPicker C={C} onClose={()=>setDrillPickerOpen(false)}
                onPick={(e)=>{ setReplyEmbeds([e]); setDrillPickerOpen(false); }}/>
            </Suspense>
          )}
          {featurePickerOpen && (
            <Suspense fallback={null}>
              <FeaturePicker C={C} onClose={()=>setFeaturePickerOpen(false)}
                onPick={(e)=>{ setReplyEmbeds([e]); setFeaturePickerOpen(false); }}/>
            </Suspense>
          )}
        </>
      )}
    </div>
  );
});
